import React, { useState, useEffect } from 'react';
import { type User, signOut } from 'firebase/auth';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { auth, db } from '../firebase';
import '../styles/dashboard.css';

interface Lead {
  id: string;
  fullName?: string;
  email?: string;
  companyName?: string;
  website?: string;
  targetCountry?: string;
  targetCity?: string;
  hqCountry?: string;
  position?: string;
  phone?: string;
  expansionTimeline?: string;
  hires?: string;
  positions?: string[];
  workspace?: string;
  aov?: string;
  aovRange?: string;
  salesCycle?: string;
  deliveryCost?: string;
  deliveryCostRange?: string;
  budget?: string;
  budgetRange?: string;
  entity?: string;
  status?: string;
  createdAt?: { toDate: () => Date } | null;
  industry?: string;
  tier?: string;
  internalNotes?: string;
  entryScore?: number;
  sector?: string;
}

type DashPage = 'overview' | 'completed' | 'inprogress' | 'simulation' | 'account' | 'multimarket' | 'newsim' | 'settings';

function getStatusLabel(lead: Lead): 'ready' | 'processing' | 'pending' {
  const s = (lead.status ?? '').toLowerCase();
  if (s === 'published' || s === 'complete' || s === 'completed') return 'ready';
  if (s === 'processing') return 'processing';
  return 'pending';
}

function formatDate(lead: Lead): string {
  const d = lead.createdAt?.toDate?.();
  if (!d) return '';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function getInitials(user: User): string {
  if (user.displayName) {
    const parts = user.displayName.trim().split(' ');
    return parts.length >= 2 ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase() : parts[0][0].toUpperCase();
  }
  return (user.email?.[0] ?? 'U').toUpperCase();
}

function getSimTitle(lead: Lead): string {
  const country = lead.targetCountry || 'Unknown Market';
  const company = lead.companyName;
  const sector = lead.sector || '';
  if (company) {
    if (sector) return `${company}  ${country} · ${sector}`;
    return `${company}  ${country}`;
  }
  if (sector) return `Market Entry  ${country} · ${sector}`;
  return `Market Entry  ${country}`;
}

function getEntryScore(lead: Lead): number | null {
  return lead.entryScore ?? null;
}

function getSector(lead: Lead): string {
  return lead.sector || lead.industry || 'General';
}

// --- Simulation Detail View ---------------------------------------------------

function SimulationDetail({ lead, onBack }: { lead: Lead; onBack: () => void }) {
  const status = getStatusLabel(lead);

  const row = (label: string, value?: string | string[]) => {
    if (!value || (Array.isArray(value) && value.length === 0)) return null;
    return (
      <div className="db-detail-row">
        <div className="db-detail-label">{label}</div>
        <div className="db-detail-value">{Array.isArray(value) ? value.join(', ') : value}</div>
      </div>
    );
  };

  return (
    <div>
      {/* Back nav */}
      <button className="db-back-btn" onClick={onBack}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
        Back to simulations
      </button>

      <div className="db-page-header" style={{ marginTop: '20px' }}>
        <div>
          <h1>{getSimTitle(lead)}</h1>
          <p>
            {lead.targetCity ? `${lead.targetCity}, ` : ''}{lead.targetCountry}
            {lead.industry ? ` · ${lead.industry}` : ''}
            {' · '}Submitted {formatDate(lead)}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
          <span className={`db-status-pill ${status}`}>
            {status === 'ready' ? '? Ready' : status === 'processing' ? '? Processing' : '? In Queue'}
          </span>
        </div>
      </div>

      {/* Status banner */}
      {status === 'pending' && (
        <div className="db-status-banner pending">
          <div className="db-status-banner-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
          </div>
          <div>
            <strong>Your simulation is in the queue.</strong>
            <p>Our team will begin building your market entry analysis within 72 hours. You'll receive an email notification the moment it's ready.</p>
          </div>
        </div>
      )}
      {status === 'processing' && (
        <div className="db-status-banner processing">
          <div className="db-status-banner-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
          </div>
          <div>
            <strong>Your simulation is being built.</strong>
            <p>Our analysts are sourcing real local data  salaries, employer taxes, setup costs, acquisition benchmarks  and modelling your full 12-month entry.</p>
          </div>
        </div>
      )}
      {status === 'ready' && (
        <div className="db-status-banner ready">
          <div className="db-status-banner-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
          </div>
          <div>
            <strong>Your simulation is ready.</strong>
            <p>Your complete market entry analysis has been delivered. Check your email for the full report link, or contact your account manager to access it.</p>
          </div>
        </div>
      )}

      {/* Detail cards */}
      <div className="db-detail-grid">
        <div className="db-detail-card">
          <div className="db-detail-card-title">Target Market</div>
          {row('Country', lead.targetCountry)}
          {row('City', lead.targetCity)}
          {row('Expansion Timeline', lead.expansionTimeline ? `${lead.expansionTimeline} months` : undefined)}
          {row('Entity Structure', lead.entity)}
        </div>

        <div className="db-detail-card">
          <div className="db-detail-card-title">Your Company</div>
          {row('Company', lead.companyName)}
          {row('Website', lead.website)}
          {row('HQ Country', lead.hqCountry)}
          {row('Your Role', lead.position)}
        </div>

        <div className="db-detail-card">
          <div className="db-detail-card-title">Team Plan</div>
          {row('Number of Hires', lead.hires)}
          {row('Roles', lead.positions)}
          {row('Workspace', lead.workspace)}
        </div>

        <div className="db-detail-card">
          <div className="db-detail-card-title">Business Data</div>
          {row('Average Order Value', lead.aov ? `${lead.aov}` : lead.aovRange)}
          {row('Sales Cycle', lead.salesCycle)}
          {row('Cost of Sale', lead.deliveryCost ? `${lead.deliveryCost}%` : lead.deliveryCostRange)}
          {row('Expansion Budget', lead.budget ? `${lead.budget}` : lead.budgetRange)}
        </div>
      </div>

      {/* Contact */}
      <div className="db-detail-card" style={{ marginTop: '20px' }}>
        <div className="db-detail-card-title">Submitted By</div>
        <div style={{ display: 'flex', gap: '40px', flexWrap: 'wrap' }}>
          {row('Name', lead.fullName)}
          {row('Email', lead.email)}
          {row('Phone', lead.phone)}
        </div>
      </div>
    </div>
  );
}

// --- Main Dashboard -----------------------------------------------------------

export default function DashboardPage({
  user,
  onNewSimulation,
}: {
  user: User;
  onNewSimulation: () => void;
}) {
  const [page, setPage] = useState<DashPage>('overview');
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user.email) { setLoading(false); return; }
    const q = query(
      collection(db, 'leads'),
      where('email', '==', user.email.toLowerCase()),
      orderBy('createdAt', 'desc')
    );
    getDocs(q)
      .then(snap => setLeads(snap.docs.map(d => ({ id: d.id, ...d.data() } as Lead))))
      .catch(() => setLeads([]))
      .finally(() => setLoading(false));
  }, [user.email]);

  const openSim = (lead: Lead) => { setSelectedLead(lead); setPage('simulation'); };
  const closeSim = () => { setSelectedLead(null); setPage('overview'); };
  const navigate = (p: DashPage) => { setSelectedLead(null); setPage(p); };

  const initials = getInitials(user);
  const displayName = user.displayName || user.email?.split('@')[0] || 'User';
  const today = new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  const ready = leads.filter(l => getStatusLabel(l) === 'ready');
  const processing = leads.filter(l => getStatusLabel(l) === 'processing');
  const pending = leads.filter(l => getStatusLabel(l) === 'pending');

  return (
    <div className="db-root">
      {/* TOPBAR */}
      <header className="topbar">
        <div className="topbar-logo">
          <svg viewBox="0 0 100 100" fill="none"><polygon points="0,0 26,0 51,66 49,66 22,32 22,100 0,100" fill="white"/><polygon points="74,0 100,0 100,100 78,100 78,38 49,66" fill="white"/></svg>
          <span>Market Entry Simulator</span>
        </div>
        <div className="topbar-right">
          <span className="topbar-date">{today}</span>
          <div className="topbar-user">
            <div className="topbar-avatar">{initials}</div>
            <span className="topbar-name">{displayName}</span>
          </div>
        </div>
      </header>

      {/* SIDEBAR */}
      <aside className="sidebar">
        <div className="sidebar-section">
          <div className="sidebar-label">Simulations</div>
          <button className={`sidebar-item ${page === 'overview' ? 'active' : ''}`} onClick={() => navigate('overview')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
            Overview
          </button>
          <button className={`sidebar-item ${page === 'completed' ? 'active' : ''}`} onClick={() => navigate('completed')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
            Completed {ready.length > 0 && <span className="badge">{ready.length}</span>}
          </button>
          <button className={`sidebar-item ${page === 'inprogress' ? 'active' : ''}`} onClick={() => navigate('inprogress')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            In Progress {(processing.length + pending.length) > 0 && <span className="badge">{processing.length + pending.length}</span>}
          </button>
        </div>
        <div className="sidebar-section">
          <div className="sidebar-label">Add-ons</div>
          <button className={`sidebar-item ${page === 'multimarket' ? 'active' : ''}`} onClick={() => navigate('multimarket')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
            Multi-Market
          </button>
        </div>
        <div className="sidebar-section">
          <div className="sidebar-label">Account</div>
          <button className={`sidebar-item ${page === 'settings' ? 'active' : ''}`} onClick={() => navigate('settings')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
            Settings
          </button>
          <button className={`sidebar-item ${page === 'account' ? 'active' : ''}`} onClick={() => navigate('account')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
            Account
          </button>
        </div>
        <div className="sidebar-bottom">
          <button className="sidebar-cta" onClick={onNewSimulation}>+ New Simulation</button>
        </div>
      </aside>

      {/* MAIN CONTENT */}
      <main className="main">

        {/* -- OVERVIEW -- */}
        {page === 'overview' && (
          <div className="page active" id="overview">
            <div className="page-header">
              <div>
                <h1>Welcome back,<br/><em>{displayName}.</em></h1>
                <p>
                  {leads.length === 0
                    ? 'Submit your first market entry simulation to get started.'
                    : ready.length > 0
                      ? `You have ${ready.length} report${ready.length !== 1 ? 's' : ''} ready${processing.length > 0 ? ` and ${processing.length} simulation${processing.length !== 1 ? 's' : ''} in progress` : ''}.`
                      : `${leads.length} simulation${leads.length !== 1 ? 's' : ''} submitted  our team is working on it.`
                  }
                </p>
              </div>
            </div>

            {/* HOW IT WORKS */}
            <div className="how-bar">
              <div style={{gridColumn:'1/-1', padding:'20px 24px 0'}}><span style={{fontFamily:'"DM Mono", monospace', fontSize:'9px', letterSpacing:'0.1em', textTransform:'uppercase', color:'var(--dim)'}}>How It Works</span></div>
              <div className="how-step">
                <div className="how-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg></div>
                <div><h3>Create a Simulation</h3><p>Pick your target country, city, team size, and timeline. We build a full analysis.</p></div>
              </div>
              <div className="how-step">
                <div className="how-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg></div>
                <div><h3>Review Your Report</h3><p>Get detailed costs, competitors, risks, hiring plans, and financial projections.</p></div>
              </div>
              <div className="how-step">
                <div className="how-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg></div>
                <div><h3>Refine with AI</h3><p>Use the AI Assistant to update data, add competitors, or compare across markets.</p></div>
              </div>
            </div>

            {/* STAT CARDS */}
            <div className="stat-row">
              <div className="stat-card">
                <div className="label">Total Simulations</div>
                <div className="value">{leads.length}</div>
                <div className="sub">{leads.length === 0 ? 'None yet' : `${leads.length} submitted`}</div>
              </div>
              <div className="stat-card">
                <div className="label">Completed Reports</div>
                <div className="value">{ready.length}</div>
                <div className="sub neutral">{ready.length > 0 ? 'Ready to review' : 'None ready yet'}</div>
              </div>
              <div className="stat-card">
                <div className="label">Latest Entry Score</div>
                {leads.length > 0 && getEntryScore(leads[0]) !== null ? (
                  <div className="value" style={{ fontSize: '28px' }}>
                    {getEntryScore(leads[0])!.toFixed(1)}
                    <span style={{ fontSize: '13px', fontFamily: '"DM Mono", monospace', fontWeight: 400, color: 'var(--dim)', marginLeft: '4px' }}>/10</span>
                  </div>
                ) : (
                  <div className="value" style={{ color: 'var(--dim)' }}>—</div>
                )}
                <div className="sub neutral">{leads.length > 0 ? `${leads[0].targetCountry || 'Global'} · ${formatDate(leads[0])}` : 'No simulations yet'}</div>
              </div>
              <div className="stat-card">
                <div className="label">Markets Compared</div>
                <div className="value">4</div>
                <div className="sub neutral">Via Multi-Market tool</div>
              </div>
            </div>

            {/* SIMULATION CARDS */}
            {loading ? (
              <div style={{textAlign:'center', padding:'40px', color:'var(--dim)'}}>Loading simulations...</div>
            ) : leads.length === 0 ? (
              <div className="db-new-sim-card">
                <div>
                  <h2>Start a new simulation.<br/><em>Know before you commit.</em></h2>
                  <p>Submit your target market and receive a complete 12-month simulation  revenue, costs, break-even timing, and multi-market comparison  within 72 hours.</p>
                </div>
                <button className="db-new-sim-btn" onClick={onNewSimulation}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  New Simulation
                </button>
              </div>
            ) : (
              <>
                <div className="section-heading">
                  <h2>Your Simulations</h2>
                  {ready.length > 0 && <button onClick={() => navigate('completed')}>VIEW ALL</button>}
                </div>
                <div className="sim-cards">
                  {leads.slice(0, 3).map(lead => {
                    const status = getStatusLabel(lead);
                    return (
                      <div className="sim-card" key={lead.id} onClick={() => openSim(lead)}>
                        <div className={`sim-card-top ${status === 'ready' ? 'green' : status === 'processing' ? 'amber' : 'grey'}`} />
                        <div className="sim-card-body">
                          <span className="sim-card-region">{lead.targetCountry || 'Market'}{lead.targetCity ? ` · ${lead.targetCity}` : ''}</span>
                          <span className={`sim-card-status ${status}`}>
                            {status === 'ready' ? 'READY' : status === 'processing' ? 'PROCESSING' : 'SUBMITTED'}
                          </span>
                          <h3>{getSimTitle(lead)}</h3>
                          <div className="sim-card-meta">
                            <div className="item">
                              <div className="lbl">Entry Score</div>
                              <div className="val">{getEntryScore(lead)?.toFixed(1) ?? '—'}</div>
                            </div>
                            <div className="item">
                              <div className="lbl">Sector</div>
                              <div className="val" style={{fontFamily:'DM Sans',fontSize:'15px',fontWeight:600}}>{getSector(lead)}</div>
                            </div>
                          </div>
                          {status === 'processing' && (
                            <div className="sim-card-progress">
                              <div className="bar-label">Analysis Generation · 65%</div>
                              <div className="bar"><div className="bar-fill" style={{width:'65%'}}></div></div>
                            </div>
                          )}
                        </div>
                        <div className="sim-card-footer">
                          <span>{formatDate(lead)}</span>
                          <button className="db-open-link" onClick={e => { e.stopPropagation(); openSim(lead); }}>
                            OPEN SIMULATION ?
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}

        {/* -- COMPLETED -- */}
        {page === 'completed' && (
          <div className="page active" id="completed">
            <div className="page-header">
              <div><h1>Completed<br/><em>Simulations.</em></h1><p>{ready.length} report{ready.length !== 1 ? 's' : ''} ready for review.</p></div>
            </div>
            {ready.length === 0 ? (
              <div style={{textAlign:'center', padding:'40px', color:'var(--dim)'}}>
                <h3>No completed reports yet</h3>
                <p>Your completed simulations will appear here once our team has finished the analysis.</p>
              </div>
            ) : (
              <div className="sim-cards">
                {ready.map(lead => {
                  const status = getStatusLabel(lead);
                  return (
                    <div className="sim-card" key={lead.id} onClick={() => openSim(lead)}>
                      <div className={`sim-card-top ${status === 'ready' ? 'green' : status === 'processing' ? 'amber' : 'grey'}`} />
                      <div className="sim-card-body">
                        <span className="sim-card-region">{lead.targetCountry || 'Market'}{lead.targetCity ? ` · ${lead.targetCity}` : ''}</span>
                        <span className={`sim-card-status ${status}`}>
                          {status === 'ready' ? 'READY' : status === 'processing' ? 'PROCESSING' : 'SUBMITTED'}
                        </span>
                        <h3>{getSimTitle(lead)}</h3>
                        <div className="sim-card-meta">
                          <div className="item">
                            <div className="lbl">Entry Score</div>
                            <div className="val">{getEntryScore(lead)?.toFixed(1) ?? '—'}</div>
                          </div>
                          <div className="item">
                            <div className="lbl">Sector</div>
                            <div className="val" style={{fontFamily:'"DM Sans", sans-serif', fontSize:'15px', fontWeight:600}}>{getSector(lead)}</div>
                          </div>
                        </div>
                      </div>
                      <div className="sim-card-footer">
                        <span>{formatDate(lead)}</span>
                        <button className="db-open-link" onClick={e => { e.stopPropagation(); openSim(lead); }}>
                          OPEN SIMULATION ?
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* -- IN PROGRESS -- */}
        {page === 'inprogress' && (
          <div className="page active" id="inprogress">
            <div className="page-header">
              <div><h1>In Progress<br/><em>Simulations.</em></h1><p>{processing.length + pending.length} simulation{processing.length + pending.length !== 1 ? 's' : ''} being worked on.</p></div>
            </div>
            {processing.length + pending.length === 0 ? (
              <div style={{textAlign:'center', padding:'40px', color:'var(--dim)'}}>
                <h3>Nothing in progress</h3>
                <p>Start a new simulation and it will appear here while our team is working on it.</p>
                <button className="db-new-sim-btn" onClick={onNewSimulation}>New Simulation</button>
              </div>
            ) : (
              <div className="sim-cards">
                {[...processing, ...pending].map(lead => {
                  const status = getStatusLabel(lead);
                  return (
                    <div className="sim-card" key={lead.id} onClick={() => openSim(lead)}>
                      <div className={`sim-card-top ${status === 'ready' ? 'green' : status === 'processing' ? 'amber' : 'grey'}`} />
                      <div className="sim-card-body">
                        <span className="sim-card-region">{lead.targetCountry || 'Market'}{lead.targetCity ? ` · ${lead.targetCity}` : ''}</span>
                        <span className={`sim-card-status ${status}`}>
                          {status === 'ready' ? 'READY' : status === 'processing' ? 'PROCESSING' : 'SUBMITTED'}
                        </span>
                        <h3>{getSimTitle(lead)}</h3>
                        <div className="sim-card-meta">
                          <div className="item">
                            <div className="lbl">Est. Ready</div>
                            <div className="val" style={{fontFamily:'"DM Sans", sans-serif', fontSize:'15px', fontWeight:600}}>2 days</div>
                          </div>
                          <div className="item">
                            <div className="lbl">Sector</div>
                            <div className="val" style={{fontFamily:'"DM Sans", sans-serif', fontSize:'15px', fontWeight:600}}>{getSector(lead)}</div>
                          </div>
                        </div>
                        {status === 'processing' && (
                          <div className="sim-card-progress">
                            <div className="bar-label">Analysis Generation · 65%</div>
                            <div className="bar"><div className="bar-fill" style={{width:'65%'}}></div></div>
                          </div>
                        )}
                      </div>
                      <div className="sim-card-footer">
                        <span>{formatDate(lead)}</span>
                        <span style={{fontSize:'11px', color:'var(--dim)'}}>Email notification set</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* -- ACCOUNT -- */}
        {page === 'account' && (
          <div className="page active" id="account">
            <div className="account-header">
              <div className="account-avatar">{initials}</div>
              <div className="account-info">
                <h2>{displayName}</h2>
                <p>{user.email}</p>
              </div>
            </div>
            <div className="stat-row">
              <div className="stat-card">
                <div className="label">Simulations Run</div>
                <div className="value">{leads.length}</div>
                <div className="sub neutral">All time</div>
              </div>
              <div className="stat-card">
                <div className="label">Reports Completed</div>
                <div className="value">{ready.length}</div>
                <div className="sub neutral">Ready to review</div>
              </div>
              <div className="stat-card">
                <div className="label">Member Since</div>
                <div className="value" style={{fontSize:'24px'}}>{user.metadata?.creationTime ? new Date(user.metadata.creationTime).getFullYear() : '2025'}</div>
                <div className="sub neutral">4 months</div>
              </div>
              <div className="stat-card">
                <div className="label">Markets Compared</div>
                <div className="value">4</div>
                <div className="sub neutral">Via Multi-Market</div>
              </div>
            </div>
            <div className="settings-grid">
              <div className="settings-card">
                <div className="settings-card-title">Account Details</div>
                <div className="settings-row">
                  <div className="info"><h4>Full name</h4></div>
                  <div style={{display:'flex', alignItems:'center', gap:'8px'}}>
                    <span className="settings-val">{displayName}</span>
                    <button className="edit-btn">Edit</button>
                  </div>
                </div>
                <div className="settings-row">
                  <div className="info"><h4>Email address</h4></div>
                  <div style={{display:'flex', alignItems:'center', gap:'8px'}}>
                    <span className="settings-val">{user.email}</span>
                    <button className="edit-btn">Edit</button>
                  </div>
                </div>
                <div className="settings-row">
                  <div className="info"><h4>Company</h4></div>
                  <div style={{display:'flex', alignItems:'center', gap:'8px'}}>
                    <span className="settings-val">Not set</span>
                    <button className="edit-btn">Edit</button>
                  </div>
                </div>
                <div className="settings-row">
                  <div className="info"><h4>Password</h4></div>
                  <button className="edit-btn">Change</button>
                </div>
              </div>
              <div className="settings-card">
                <div className="settings-card-title">Preferences</div>
                <div className="settings-row">
                  <div className="info"><h4>Report language</h4></div>
                  <div className="settings-val">English</div>
                </div>
                <div className="settings-row">
                  <div className="info"><h4>Currency</h4></div>
                  <div className="settings-val">EUR ()</div>
                </div>
                <div className="settings-row">
                  <div className="info"><h4>Email notifications</h4></div>
                  <div className="settings-val green">On</div>
                </div>
                <div className="settings-row">
                  <div className="info"><h4>Weekly digest</h4></div>
                  <div className="settings-val green">On</div>
                </div>
              </div>
            </div>
            <div style={{marginTop:'32px'}}>
              <button onClick={() => signOut(auth)}
                style={{ padding: '12px 28px', border: '1.5px solid var(--rule)', borderRadius: '10px', background: 'transparent', fontSize: '14px', fontWeight: 500, fontFamily: '"DM Sans", sans-serif', color: 'var(--text-mid)', cursor: 'pointer', transition: 'all 0.15s' }}>
                Sign Out
              </button>
            </div>
          </div>
        )}

        {/* -- SETTINGS -- */}
        {page === 'settings' && (
          <div className="page active" id="settings">
            <div className="page-header">
              <div><h1>Platform<br/><em>Settings.</em></h1><p>Manage your notifications, integrations, and report preferences.</p></div>
            </div>
            <div className="settings-grid">
              <div className="settings-card">
                <div className="settings-card-title">Notifications</div>
                <div className="settings-row">
                  <div className="info"><h4>Report ready</h4><p>Email when simulation completes</p></div>
                  <div className="toggle on" onClick={(e) => e.currentTarget.classList.toggle('on')}></div>
                </div>
                <div className="settings-row">
                  <div className="info"><h4>Live update alerts</h4><p>Email on high-impact events</p></div>
                  <div className="toggle" onClick={(e) => e.currentTarget.classList.toggle('on')}></div>
                </div>
                <div className="settings-row">
                  <div className="info"><h4>Weekly digest</h4><p>Summary of market changes</p></div>
                  <div className="toggle on" onClick={(e) => e.currentTarget.classList.toggle('on')}></div>
                </div>
                <div className="settings-row">
                  <div className="info"><h4>Marketing emails</h4><p>New features and case studies</p></div>
                  <div className="toggle" onClick={(e) => e.currentTarget.classList.toggle('on')}></div>
                </div>
              </div>
              <div className="settings-card">
                <div className="settings-card-title">Report Preferences</div>
                <div className="settings-row">
                  <div className="info"><h4>Report language</h4></div>
                  <div className="settings-val">English</div>
                </div>
                <div className="settings-row">
                  <div className="info"><h4>Currency</h4></div>
                  <div className="settings-val">EUR ()</div>
                </div>
                <div className="settings-row">
                  <div className="info"><h4>Scenario depth</h4><p>Number of scenarios modelled</p></div>
                  <div className="settings-val">3 (standard)</div>
                </div>
                <div className="settings-row">
                  <div className="info"><h4>Auto-save changes</h4><p>Save report assistant edits automatically</p></div>
                  <div className="toggle on" onClick={(e) => e.currentTarget.classList.toggle('on')}></div>
                </div>
              </div>
            </div>
            <div className="settings-grid">
              <div className="settings-card">
                <div className="settings-card-title">Account Details</div>
                <div className="settings-row">
                  <div className="info"><h4>Full name</h4></div>
                  <div style={{display:'flex', alignItems:'center', gap:'8px'}}>
                    <span className="settings-val">{displayName}</span>
                    <button className="edit-btn">Edit</button>
                  </div>
                </div>
                <div className="settings-row">
                  <div className="info"><h4>Email address</h4></div>
                  <div style={{display:'flex', alignItems:'center', gap:'8px'}}>
                    <span className="settings-val">{user.email}</span>
                    <button className="edit-btn">Edit</button>
                  </div>
                </div>
                <div className="settings-row">
                  <div className="info"><h4>Company</h4></div>
                  <div style={{display:'flex', alignItems:'center', gap:'8px'}}>
                    <span className="settings-val">Not set</span>
                    <button className="edit-btn">Edit</button>
                  </div>
                </div>
                <div className="settings-row">
                  <div className="info"><h4>Password</h4></div>
                  <button className="edit-btn">Change</button>
                </div>
              </div>
              <div className="settings-card">
                <div className="settings-card-title">Subscription & Billing</div>
                <div className="settings-row">
                  <div className="info"><h4>Current plan</h4></div>
                  <div className="settings-val green">STANDARD</div>
                </div>
                <div className="settings-row">
                  <div className="info"><h4>Simulations included</h4></div>
                  <div className="settings-val">3 / month</div>
                </div>
                <div className="settings-row">
                  <div className="info"><h4>Next billing date</h4></div>
                  <div className="settings-val">1 Mar 2026</div>
                </div>
                <div className="settings-row">
                  <div className="info"><h4>Active add-ons</h4></div>
                  <div className="settings-val amber">None</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* -- MULTI-MARKET -- */}
        {page === 'multimarket' && (
          <div className="page active" id="multimarket">
            <div className="page-header">
              <div><h1>Multi-Market<br/><em>Comparison.</em></h1><p>Compare your expansion potential across multiple markets simultaneously.</p></div>
            </div>
            
            <div className="multimarket-header">
              <div className="multimarket-sim">
                <h3>SIMULATION</h3>
                <div className="multimarket-sim-info">
                  <div className="multimarket-sim-title">Select a simulation to compare markets</div>
                  <button className="compare-btn" onClick={() => navigate('completed')}>Select Simulation</button>
                </div>
              </div>
            </div>

            <div className="multimarket-section">
              <h2>Market Comparison</h2>
              <div className="multimarket-subtitle">Choose a completed simulation to compare markets</div>
              
              {ready.length === 0 ? (
                <div style={{textAlign:'center', padding:'60px 20px', color:'var(--dim)'}}>
                  <h3>No completed simulations</h3>
                  <p>Complete a simulation first to compare markets across different regions.</p>
                  <button className="compare-btn" onClick={onNewSimulation}>Start New Simulation</button>
                </div>
              ) : (
                <div className="market-cards">
                  {ready.slice(0, 4).map((lead, index) => {
                    // Generate market comparison for this specific simulation
                    const markets = [
                      { name: lead.targetCountry || 'Germany', entryScore: (lead.entryScore || 7.5).toFixed(1), tam: '42.3B', icpMatch: 92, risk: 'Medium', riskColor: 'amber' },
                      { name: 'Netherlands', entryScore: '7.8', tam: '18.7B', icpMatch: 88, risk: 'Low', riskColor: 'green' },
                      { name: 'USA', entryScore: '7.2', tam: '$156.2B', icpMatch: 76, risk: 'Low', riskColor: 'green' },
                      { name: 'Brazil', entryScore: '6.9', tam: 'R$78.4B', icpMatch: 71, risk: 'Medium', riskColor: 'amber' }
                    ];
                    
                    // Shuffle but keep target country as winner
                    const shuffled = [...markets].sort(() => Math.random() - 0.5);
                    const targetIndex = shuffled.findIndex(m => m.name === lead.targetCountry);
                    if (targetIndex > 0) {
                      [shuffled[0], shuffled[targetIndex]] = [shuffled[targetIndex], shuffled[0]];
                    }
                    
                    const marketData = shuffled[0];
                    return (
                      <div className={`market-card ${index === 0 ? 'winner' : ''}`} key={lead.id}>
                        <div className="market-card-header">
                          <div className="market-name">{marketData.name}</div>
                          {index === 0 && <div className="market-badge winner">Recommended  Winner</div>}
                        </div>
                        <div className="market-card-body">
                          <div className="market-metric">
                            <div className="metric-label">Entry Score</div>
                            <div className="metric-value">{marketData.entryScore}</div>
                          </div>
                          <div className="market-metric">
                            <div className="metric-label">Market Size (TAM)</div>
                            <div className="metric-value">{marketData.tam}</div>
                          </div>
                          <div className="market-metric">
                            <div className="metric-label">ICP Match</div>
                            <div className="metric-value">{marketData.icpMatch}%</div>
                          </div>
                          <div className="market-metric">
                            <div className="metric-label">Regulatory Risk</div>
                            <div className={`metric-value ${marketData.riskColor}`}>{marketData.risk}</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* -- NEW SIMULATION -- */}
        {page === 'newsim' && (
          <div className="page active" id="newsim">
            <div className="page-header">
              <div><h1>New Simulation.<br/><em>Market Entry.</em></h1><p>Tell us where you want to expand, how fast, and what team you need. We simulate everything.</p></div>
            </div>
            <div style={{textAlign:'center', padding:'40px', color:'var(--dim)'}}>
              <h3>New Simulation Form</h3>
              <p>Simulation form will be implemented here. For now, use the main intake flow.</p>
              <button className="db-new-sim-btn" onClick={onNewSimulation}>Go to Intake Form</button>
            </div>
          </div>
        )}

      </main>
    </div>
  );
}
